import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, analytics, logEvent } from '../firebase';
import { getAuth, signOut } from 'firebase/auth';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ReactECharts from 'echarts-for-react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo1.png';
import '../styles/Manage.css';
import { FaArrowLeft, FaArrowRight, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

const palette = ['#8e44ad', '#27ae60', '#e74c3c', '#7f8c8d', '#3498db', '#e67e22', '#2980b9', '#f39c12', '#34495e'];

const Results = () => {
  // Data + UI state
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters] = useState({ from: '', to: '' }); // made writable
  const [chartFilter, setChartFilter] = useState({ pest: null, location: null, farmer: null, month: null, day: null,}); // interactive chart filters
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'asc' });
  const [userSortConfig, setUserSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const reportsPerPage = 5;
  const [userPage, setUserPage] = useState(1);
  const usersPerPage = 5;
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();

  // Show modal confirmation
  const confirmLogout = (e) => {
    e.preventDefault();
    setShowLogoutModal(true);
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('user');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Fetch data once
  useEffect(() => {
    logEvent(analytics, 'view_results_page');
    const fetchReports = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'pestScans'));
        const fetchedReports = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          locationAddress: doc.data().locationAddress || 'Unknown',
        }));
        const validReports = fetchedReports.filter(r => r.result !== "Unidentified");
        setReports(validReports);
      } catch (error) {
        console.error('Error fetching reports:', error);
      }
    };
    fetchReports();

    const fetchUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        const fetchedUsers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(fetchedUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Sorting functions for tables
  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const handleUserSort = (key) => {
    setUserSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="sort-icon" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="sort-icon" />
      : <FaSortDown className="sort-icon" />;
  };

  const renderUserSortIcon = (key) => {
    if (userSortConfig.key !== key) return <FaSort className="sort-icon" />;
    return userSortConfig.direction === 'asc'
      ? <FaSortUp className="sort-icon" />
      : <FaSortDown className="sort-icon" />;
  };

  // Filtered reports: date filter (from/to) + interactive chart filter
  const filteredReports = useMemo(() => {
    let result = [...reports];
    const { from, to } = filters;

    if (from || to) {
      logEvent(analytics, 'filter_date', { from: from || 'none', to: to || 'none' });
      result = result.filter(report => {
        const reportDate = new Date(report.date);
        const fromDate = from ? new Date(from) : null;
        const toDate = to ? new Date(to) : null;
        return (!fromDate || reportDate >= fromDate) && (!toDate || reportDate <= toDate);
      });
    }

    if (chartFilter.pest) {
      result = result.filter(r => r.result === chartFilter.pest);
    }
    if (chartFilter.location) {
      result = result.filter(r => r.locationAddress === chartFilter.location);
    }
    if (chartFilter.farmer) {
      result = result.filter(r => r.farmerName === chartFilter.farmer);
    }
    if (chartFilter.month) {
result = result.filter(r => {
  const monthLabel = new Date(r.date).toLocaleString("default", {
    month: "short",
    year: "numeric"
  });
  return monthLabel === chartFilter.month;
});

    }
if (chartFilter.day) {
  result = result.filter(r => {
    const dayKey = new Date(r.date).toISOString().split("T")[0];
    return dayKey === chartFilter.day;
  });
}

    // sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const valA = a[sortConfig.key] ?? '';
        const valB = b[sortConfig.key] ?? '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [reports, filters, chartFilter, sortConfig]);

  const totalPages = Math.ceil(filteredReports.length / reportsPerPage);

  const currentReports = useMemo(() => {
    const start = (currentPage - 1) * reportsPerPage;
    return filteredReports.slice(start, start + reportsPerPage);
  }, [filteredReports, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // USERS pagination/sorting
  const userTotalPages = Math.ceil(users.length / usersPerPage);
  const sortedUsers = useMemo(() => {
    let sorted = [...users];
    if (userSortConfig.key) {
      sorted.sort((a, b) => {
        const valA = a[userSortConfig.key] ?? '';
        const valB = b[userSortConfig.key] ?? '';
        if (valA < valB) return userSortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return userSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [users, userSortConfig]);

  const currentUsers = useMemo(() => {
    const start = (userPage - 1) * usersPerPage;
    return sortedUsers.slice(start, start + usersPerPage);
  }, [sortedUsers, userPage]);

  const goToUserPage = (page) => {
    if (page < 1 || page > userTotalPages) return;
    setUserPage(page);
  };

  // Export Reports PDF (unchanged)
  const exportPDF = async () => {
    logEvent(analytics, 'export_pdf');
    const querySnapshot = await getDocs(collection(db, "pestScans"));
    const allReports = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: [1000, 1200] });

    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 300;
    const logoHeight = logoWidth / (1080 / 233);
    const centerX = (pageWidth - logoWidth) / 2;

    doc.addImage(logo, 'PNG', centerX, 10, logoWidth, logoHeight);

    autoTable(doc, {
      head: [[
        "Date", "Time", "Farmer Name", "Location", "Pest Result",
        "Latitude", "Longitude", "Species", "Family",
        "Order", "Damage & Symptoms", "Treatment", "Image"
      ]],
      body: allReports.map(r => [
        new Date(r.date).toLocaleDateString(),
        r.time || "N/A",
        r.farmerName || "Unknown",
        r.locationAddress,
        r.result,
        r.latitude ?? "N/A",
        r.longitude ?? "N/A",
        r.details?.species || "N/A",
        r.details?.family || "N/A",
        r.details?.order || "N/A",
        Array.isArray(r.details?.damageCharacteristics)
          ? r.details.damageCharacteristics.map(d => `- ${d}`).join("\n\n")
          : "N/A",
        Array.isArray(r.details?.treatmentRecommendations)
          ? r.details.treatmentRecommendations
              .map(t => `${t.category}:\n- ${t.methods.join("\n- ")}`)
              .join("\n\n")
          : "N/A",
        r.image ? "View Image" : "N/A"
      ]),
      startY: logoHeight + 20,
      styles: { fontSize: 8 },
      columnStyles: {
        11: { cellWidth: 150 },
        12: { cellWidth: 200 },
      },
    });

    doc.save('all-pest-reports.pdf');
  };

  // Export Users PDF (unchanged)
  const exportUsersPDF = () => {
    logEvent(analytics, 'export_users_pdf');

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 300;
    const logoHeight = logoWidth / (1080 / 233);

    const centerX = (pageWidth - logoWidth) / 2;
    doc.addImage(logo, 'PNG', centerX, 10, logoWidth, logoHeight);

    autoTable(doc, {
      html: '#users-table',
      startY: logoHeight + 20
    });
    doc.save('users-list.pdf');
  };

  // Export CSV handlers (unchanged)
  const handleExportCSV = () => {
    logEvent(analytics, 'export_csv');
  };
  const handleExportUsersCSV = () => {
    logEvent(analytics, 'export_users_csv');
  };

  // CSV data helpers (unchanged)
  const getExportData = () => {
    return reports.map((report) => ({
      Date: report.date || "N/A",
      Time: report.time || "N/A",
      FarmerName: report.farmerName || "N/A",
      Latitude: report.latitude || "N/A",
      Longitude: report.longitude || "N/A",
      Result: report.result || "N/A",
      Species: report.details?.species || "N/A",
      Family: report.details?.family || "N/A",
      Order: report.details?.order || "N/A",
      DamageCharacteristics: Array.isArray(report.details?.damageCharacteristics)
        ? report.details.damageCharacteristics.join("\n")
        : "N/A",
      TreatmentRecommendations: Array.isArray(report.details?.treatmentRecommendations)
        ? report.details.treatmentRecommendations
            .map(t => `${t.category}: ${t.methods.join(", ")}`)
            .join("\n")
        : "N/A",
      ImageLink: report.image || "N/A"
    }));
  };

  const getExportUser = () => {
    return users.map((user) => ({
      Name: user.name || "N/A",
      Email: user.email || "N/A",
      ContactNumber: user.contactNumber || "N/A",
      Sex: user.sex || "N/A",
      CreatedAt: user.createdAt?.seconds
        ? new Date(user.createdAt.seconds * 1000).toLocaleString()
        : "N/A"
    }));
  };

  // -------------------------
  // Analytics data (ECharts)
  // -------------------------

  // Pest counts (pie)
  const pestCounts = useMemo(() => {
    return filteredReports.reduce((acc, { result }) => {
      acc[result] = (acc[result] || 0) + 1;
      return acc;
    }, {});
  }, [filteredReports]);

  // Monthly counts (bar)
  const monthlyCounts = useMemo(() => {
    return filteredReports.reduce((acc, { date }) => {
      const month = new Date(date).toLocaleString('default', { month: 'short', year: 'numeric' });
      acc[month] = (acc[month] || 0) + 1;
      return acc;
    }, {});
  }, [filteredReports]);

  // Daily counts (line)
  const dailyCounts = useMemo(() => {
    return filteredReports.reduce((acc, { date }) => {
      const day = new Date(date).toISOString().split('T')[0];
      acc[day] = (acc[day] || 0) + 1;
      return acc;
    }, {});
  }, [filteredReports]);

  // Farmer counts (horizontal bar)
  const farmerCounts = useMemo(() => {
    return filteredReports.reduce((acc, { farmerName }) => {
      acc[farmerName] = (acc[farmerName] || 0) + 1;
      return acc;
    }, {});
  }, [filteredReports]);

  // Stacked pest per month
  const pestPerMonth = useMemo(() => {
    const result = {};
    filteredReports.forEach(({ result: pest, date }) => {
      const month = new Date(date).toLocaleString('default', { month: 'short', year: 'numeric' });
      if (!result[month]) result[month] = {};
      result[month][pest] = (result[month][pest] || 0) + 1;
    });
    return result;
  }, [filteredReports]);

  const stackedLabels = Object.keys(pestPerMonth);
  const pestTypes = useMemo(() => {
    return [...new Set(filteredReports.map(r => r.result))];
  }, [filteredReports]);

  // Location counts (doughnut)
  const locationCounts = useMemo(() => {
    return filteredReports.reduce((acc, { locationAddress }) => {
      acc[locationAddress] = (acc[locationAddress] || 0) + 1;
      return acc;
    }, {});
  }, [filteredReports]);

  // Radar (pest frequency per farmer)
  const pestPerFarmer = useMemo(() => {
    const result = {};
    filteredReports.forEach(({ farmerName, result: pest }) => {
      if (!result[farmerName]) result[farmerName] = {};
      result[farmerName][pest] = (result[farmerName][pest] || 0) + 1;
    });
    return result;
  }, [filteredReports]);

  const farmerNames = Object.keys(pestPerFarmer);

  // KPI summary stats
  const totalReports = filteredReports.length;
  const uniqueFarmers = farmerNames.length;

  // Pie (pest distribution)
  const pieOption = useMemo(() => {
    const labels = Object.keys(pestCounts);
    const data = labels.map((l, i) => ({ name: l, value: pestCounts[l] }));
    return {
      tooltip: { trigger: 'item' },
      legend: { top: '6%', left: 'center' },
      series: [
        {
          name: 'Pest Distribution',
          type: 'pie',
          radius: '55%',
          center: ['50%', '54%'],
          data,
          emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.3)' } },
          label: { formatter: '{b}: {c} ({d}%)' },
          color: palette,
        }
      ]
    };
  }, [pestCounts]);

  // Monthly bar
const monthlyOption = useMemo(() => {
  const labels = Object.keys(monthlyCounts);
  const values = labels.map(l => monthlyCounts[l]);

  return {
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: labels },
    yAxis: { type: 'value' },
    series: [{
      data: values,
      type: 'bar',
      barMaxWidth: 36,
      itemStyle: { color: palette[4] }
    }],
    grid: { left: '3%', right: '4%', bottom: '6%', containLabel: true },
    // CLICK HANDLER
    onClick: (params) => {
      const month = params.name;
      setChartFilter(prev => ({
        ...prev,
        month: prev.month === month ? null : month,
        day: null   // reset day filter when month chosen
      }));
      logEvent(analytics, "chart_filter_month", { month });
    }
  };
}, [monthlyCounts]);

  // Daily line
  const dailyOption = useMemo(() => {
    const labels = Object.keys(dailyCounts).sort();
    const values = labels.map(l => dailyCounts[l]);

    return {
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: labels, boundaryGap: false },
      yAxis: { type: 'value' },
      series: [{
        data: values,
        type: 'line',
        smooth: true,
        areaStyle: {},
        symbol: 'circle'
      }],
      grid: { left: '3%', right: '4%', bottom: '6%', containLabel: true },
      // CLICK HANDLER
      onClick: (params) => {
        const selectedDay = params.name; // YYYY-MM-DD
        setChartFilter(prev => ({
          ...prev,
          day: prev.day === selectedDay ? null : selectedDay,
          month: null  // reset month filter for clarity
        }));
        logEvent(analytics, "chart_filter_day", { day: selectedDay });
      }
    };
  }, [dailyCounts]);

  // Farmer horizontal bar
  const farmerOption = useMemo(() => {
    const labels = Object.keys(farmerCounts);
    const values = labels.map(l => farmerCounts[l]);
    return {
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: labels, axisLabel: { interval: 0 } },
      series: [{
        type: 'bar',
        data: values,
        barMaxWidth: 20,
        itemStyle: { color: palette[1] },
      }],
      grid: { left: '12%', right: '4%', bottom: '6%', containLabel: true }
    };
  }, [farmerCounts]);

  // Doughnut (location)
const doughnutOption = useMemo(() => {
  const labels = Object.keys(locationCounts);
  const data = labels.map((l) => ({
    name: l,
    value: locationCounts[l]
  }));

  return {
    tooltip: { trigger: 'item' },

    // Legend at bottom – clean, scrollable, no overlap
    legend: {
      bottom: 8,
      left: 'center',
      orient: 'horizontal',
      type: 'scroll',                    // enables left/right scroll if many locations
      icon: 'circle',
      itemWidth: 12,
      itemHeight: 12,
      itemGap: 16,
      textStyle: {
        fontSize: 11.5,
        color: '#333',
        width: 160,                     // forces wrapping
        overflow: 'truncate'            // adds "…" automatically
      },
      formatter: (name) => {
        return name.length > 35 ? name.substring(0, 32) + '...' : name;
      },
      tooltip: { show: true }           // hover to see full location name
    },

    series: [
      {
        name: 'Reports by Location',
        type: 'pie',
        radius: ['38%', '68%'],         // slightly thinner ring = more elegant
        center: ['50%', '42%'],         // moved up a bit to give legend space
        avoidLabelOverlap: false,
        label: { show: false },
        emphasis: {
          label: { show: true, fontSize: 14, fontWeight: 'bold' }
        },
        labelLine: { show: false },
        data,
        color: palette,
      }
    ]
  };
}, [locationCounts]);

const onChartClick = useCallback((params) => {
  if (!params) return;

  const { seriesType, name } = params;

  // PIE → pest or location filter
  if (seriesType === "pie") {
    if (pestCounts[name] !== undefined) {
      setChartFilter(prev => ({
        ...prev,
        pest: prev.pest === name ? null : name
      }));
      logEvent(analytics, "chart_filter_pest", { pest: name });
    } 
    else if (locationCounts[name] !== undefined) {
      setChartFilter(prev => ({
        ...prev,
        location: prev.location === name ? null : name
      }));
      logEvent(analytics, "chart_filter_location", { location: name });
    }
  }

  // BAR → farmer or month filter
  else if (seriesType === "bar") {
    // Farmer bar chart
    if (farmerCounts[name] !== undefined) {
      setChartFilter(prev => ({
        ...prev,
        farmer: prev.farmer === name ? null : name,
      }));
      logEvent(analytics, "chart_filter_farmer", { farmer: name });
    }

    // Month bar chart (ex: "Jan 2025")
    else if (monthlyCounts && monthlyCounts[name] !== undefined) {
      setChartFilter(prev => ({
        ...prev,
        month: prev.month === name ? null : name,
        day: null // reset daily filter
      }));
      logEvent(analytics, "chart_filter_month", { month: name });
    }
  }

  // LINE → daily date filter
  else if (seriesType === "line") {
    if (dailyCounts && dailyCounts[name] !== undefined) {
      setChartFilter(prev => ({
        ...prev,
        day: prev.day === name ? null : name,
        month: null // reset month filter
      }));
      logEvent(analytics, "chart_filter_day", { day: name });
    }
  }
}, [
  pestCounts,
  locationCounts,
  farmerCounts,
  monthlyCounts,
  dailyCounts
]);


  useEffect(() => {
    logEvent(analytics, 'filter_pest_distribution', { unique_pests: Object.keys(pestCounts).length });
    logEvent(analytics, 'farmer_frequency_calculated', { unique_farmers: Object.keys(farmerCounts).length });
    logEvent(analytics, 'daily_trend_generated', { days: Object.keys(dailyCounts).length });
    logEvent(analytics, 'stacked_bar_prepared', { months: stackedLabels.length, pests: pestTypes.length });
  }, [pestCounts, farmerCounts, dailyCounts, stackedLabels, pestTypes]);

const chartCardStyle = {
  background: "#fff",
  padding: 16,
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  minHeight: 380,
  display: "flex",
  flexDirection: "column",
  width: "100%",           // ← critical
  boxSizing: "border-box"  // ← critical
};

const chartTitleStyle = {
  margin: "0 0 12px 0",
  fontWeight: 600,
  textAlign: "center",
  letterSpacing: '0.8px'
};

  return (
    <div className="manage-page">
      <header className="header-bar">
        <img src={logo} className="header-logo" alt="OnionMap" />
        <nav className="menu">
          <ul className="menu-list">
            <li><Link to="/home">HOME</Link></li>
            <li><Link to="/results">RESULTS</Link></li>
            <li><Link to="/about-us">ABOUT US</Link></li>
            <li>
              <Link
                to="#"
                onClick={confirmLogout}
                className="logout-link"
              >
                LOGOUT
              </Link>
            </li>
          </ul>
        </nav>
      </header>

      {/* Data Analytics Heading */}
      <h2 className="summary-title">Data Analytics</h2>

      {/* BEAUTIFUL SUMMARY STATISTICS — FINAL VERSION */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '20px',
        padding: '32px 20px',
        margin: '0 16px 32px',
      }}>

        {/* 1. Total Reports */}
        <div style={{
          background: 'white',
          padding: '28px 20px',
          borderRadius: '18px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
          textAlign: 'center',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-6px)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
          <div style={{ fontSize: '56px', fontWeight: 'bold', color: '#8e44ad', marginBottom: '12px' }}>
            {totalReports}
          </div>
          <div style={{ fontSize: '36px', color: '#8e44ad', marginBottom: '12px' }}>
            <i className="fas fa-chart-bar"></i>
          </div>
          <div style={{ color: '#000000ff', fontSize: '15px', fontWeight: '600', letterSpacing: '0.8px' }}>
            TOTAL REPORTS
          </div>
        </div>

        {/* 2. Unique Users */}
        <div style={{
          background: 'white',
          padding: '28px 20px',
          borderRadius: '18px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
          textAlign: 'center',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-6px)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
          <div style={{ fontSize: '56px', fontWeight: 'bold', color: '#27ae60', marginBottom: '12px' }}>
            {uniqueFarmers}
          </div>
          <div style={{ fontSize: '36px', color: '#27ae60', marginBottom: '12px' }}>
            <i className="fas fa-users"></i>
          </div>
          <div style={{ color: '#000000ff', fontSize: '15px', fontWeight: '600', letterSpacing: '0.8px' }}>
            UNIQUE USERS
          </div>
        </div>

        {/* 3. Reports Today */}
        <div style={{
          background: 'white',
          padding: '28px 20px',
          borderRadius: '18px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
          textAlign: 'center',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-6px)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
          <div style={{ fontSize: '56px', fontWeight: 'bold', color: '#e67e22', marginBottom: '12px' }}>
            {(() => {
              const today = new Date();
              const todayStr = today.toISOString().split('T')[0];
              return filteredReports.filter(r => {
                const reportDate = new Date(r.date);
                return reportDate.toISOString().split('T')[0] === todayStr;
              }).length;
            })()}
          </div>
          <div style={{ fontSize: '36px', color: '#e67e22', marginBottom: '12px' }}>
            <i className="fas fa-sun"></i>
          </div>
          <div style={{ color: '#000000ff', fontSize: '15px', fontWeight: '600', letterSpacing: '0.8px' }}>
            REPORTS TODAY
          </div>
        </div>

        {/* 4. Most Common Pest */}
        <div style={{
          background: 'white',
          padding: '28px 20px',
          borderRadius: '18px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
          textAlign: 'center',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-6px)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
          <div style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#e74c3c',
            marginBottom: '8px',
            lineHeight: '1.3',
            minHeight: '68px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 8px'
          }}>
            {Object.keys(pestCounts).length > 0
              ? Object.entries(pestCounts)
                  .sort(([,a], [,b]) => b - a)[0][0]
              : 'No Data'}
          </div>
          <div style={{ fontSize: '36px', color: '#e74c3c', marginBottom: '12px' }}>
            <i className="fas fa-bug"></i>
          </div>
          <div style={{ color: '#000000ff', fontSize: '15px', fontWeight: '600', letterSpacing: '0.8px' }}>
            MOST COMMON PEST
          </div>
        </div>
      </div>

      {/* Your Original 4-Chart Grid - UNCHANGED */}
            <div 
        className="analytics-container grid-layout" 
        style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",   // 4 equal columns, always
          gridTemplateRows: "1fr",                 // forces exactly 1 row
          gap: 24,
          padding: "0 16px",
          marginBottom: 20,
          overflowX: "auto",                       // allows horizontal scroll on small screens
          paddingBottom: 8                         // tiny fix for scroll shadow
        }}>
        {[
          { title: "PEST DISTRIBUTION", option: pieOption, clickEvent: onChartClick },
          { title: "MONTHLY REPORTS", option: monthlyOption, clickEvent: onChartClick },
          { title: "DAILY REPORTS", option: dailyOption, clickEvent: onChartClick },
          { title: "USER SUBMISSION REPORTS", option: farmerOption, clickEvent: onChartClick },
        ].map((chart, idx) => (
          <div key={idx} style={{ ...chartCardStyle }}>
            <h3 style={chartTitleStyle}>{chart.title}</h3>
            <div style={{ flex: 1 }}>
              <ReactECharts
                option={chart.option}
                style={{ width: "100%", height: "100%" }}
                onEvents={chart.clickEvent ? { click: chart.clickEvent } : {}}
              />
            </div>
          </div>
        ))}
      </div>

      {/* MERGED SECTION: Table (left) + Reports by Location (right) */}
      <div style={{ 
        padding: "0 16px",           // ← same padding as top charts
        marginTop: 32,
        display: 'flex', 
        gap: 32, 
        flexWrap: 'wrap', 
        alignItems: 'flex-start'   // ← this + marginTop below = perfect alignment
      }}>

        {/* Left: Database Table */}
        <div style={{ flex: '2 1 650px', marginTop: 1 }}>
          <div className="table-container">
            <table id="report-table" className="results-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('date')}>Date {renderSortIcon('date')}</th>
                  <th onClick={() => handleSort('time')}>Time {renderSortIcon('time')}</th>
                  <th onClick={() => handleSort('farmerName')}>Farmer Name {renderSortIcon('farmerName')}</th>
                  <th onClick={() => handleSort('locationAddress')}>Location {renderSortIcon('locationAddress')}</th>
                  <th onClick={() => handleSort('result')}>Pest Result {renderSortIcon('result')}</th>
                  <th>Image</th>
                </tr>
              </thead>
              <tbody>
                {currentReports.map(report => (
                  <tr key={report.id}>
                    <td>{new Date(report.date).toLocaleDateString()}</td>
                    <td>{report.time || 'N/A'}</td>
                    <td>{report.farmerName || 'Unknown'}</td>
                    <td>{report.locationAddress}</td>
                    <td>{report.result}</td>
                    <td>
                    {report.image ? (
                      <a href={report.image} target="_blank" rel="noopener noreferrer">
                        View Image
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination1" style={{ marginTop: 1 }}>
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="arrow-btn-circle">
                <FaArrowLeft />
              </button>
              <span className="page-indicator animated">Page {currentPage} of {totalPages}</span>
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="arrow-btn-circle">
                <FaArrowRight />
              </button>
            </div>
          )}

          {/* Export Buttons */}
          <div className="export-buttons" style={{ marginTop: 1 }}>
            <CSVLink data={getExportData()} filename="pest-reports.csv" onClick={handleExportCSV}>
              <button>Export CSV</button>
            </CSVLink>
            <button onClick={exportPDF}>Export PDF</button>
          </div>
        </div>

    {/* Right: Reports by Location */}
      <div style={{ flex: '1 1 380px', minWidth: 320, marginTop: 20 }}>
        <div style={{ ...chartCardStyle, minHeight: 480 }}>
          <h3 style={chartTitleStyle}>REPORTS BY LOCATION</h3>
          <div style={{ flex: 1, position: 'relative' }}>
            <ReactECharts
              option={doughnutOption}
              style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0 }}
              onEvents={{ click: onChartClick }}
            />
          </div>
        </div>
      </div>
    </div>

      {/* USERS SECTION */}
      <h2 className="summary-title" style={{ marginTop: 28 }}>Registered Users</h2>
      <div className="users-table-container" style={{ marginBottom: 12 }}>
        <table id="users-table">
          <thead>
            <tr>
              <th onClick={() => handleUserSort('name')}>
                Name {renderUserSortIcon('name')}
              </th>
              <th onClick={() => handleUserSort('email')}>
                Email {renderUserSortIcon('email')}
              </th>
              <th onClick={() => handleUserSort('contactNumber')}>
                Contact Number {renderUserSortIcon('contactNumber')}
              </th>
              <th onClick={() => handleUserSort('sex')}>
                Gender {renderUserSortIcon('sex')}
              </th>
              <th onClick={() => handleUserSort('createdAt')}>
                Created At {renderUserSortIcon('createdAt')}
              </th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.name || 'N/A'}</td>
                  <td>{user.email || 'N/A'}</td>
                  <td>{user.contactNumber || 'N/A'}</td>
                  <td>{user.sex || 'N/A'}</td>
                  <td>
                    {user.createdAt?.seconds
                      ? new Date(user.createdAt.seconds * 1000).toLocaleString()
                      : 'N/A'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center' }}>No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Users pagination */}
      {userTotalPages > 1 && (
        <div className="pagination1">
          <button
            onClick={() => goToUserPage(userPage - 1)}
            disabled={userPage === 1}
            className="arrow-btn-circle"
          >
            <FaArrowLeft />
          </button>

          <span key={userPage} className="page-indicator animated">
            Page {userPage} of {userTotalPages}
          </span>

          <button
            onClick={() => goToUserPage(userPage + 1)}
            disabled={userPage === userTotalPages}
            className="arrow-btn-circle"
          >
            <FaArrowRight />
          </button>
        </div>
      )}

      {/* EXPORT BUTTONS FOR USERS */}
      <div className="export-buttons" style={{ marginTop: 12 }}>
        <CSVLink data={getExportUser()} filename="users-list.csv" onClick={handleExportUsersCSV}>
          <button>Export CSV</button>
        </CSVLink>
        <button onClick={exportUsersPDF}>Export PDF</button>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="delete-confirm-modal">
          <div className="delete-confirm-content">
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to log out of your account?</p>
            <div className="delete-confirm-buttons">
              <button
                className="delete-confirm-btn confirm"
                onClick={handleLogout}
              >
                Confirm
              </button>
              <button
                className="delete-confirm-btn cancel"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Results;